import {
  COOKIE_CONSENT_CHANGED_EVENT,
  hasAnalyticsConsent,
  type CookieConsentPreferences
} from './cookieConsent';
import type { AnalyticsEventType, AnalyticsMetadataValue, AnalyticsPageCategory, AnalyticsEntityType } from './analytics';
import type { SupportedLocale } from '../types';

const GA_MEASUREMENT_ID =
  import.meta.env.VITE_GA_MEASUREMENT_ID ||
  import.meta.env.VITE_GOOGLE_ANALYTICS_ID ||
  '';

const isBrowser = typeof window !== 'undefined';

type GtagCommand =
  | ['js', Date]
  | ['config', string, Record<string, unknown>?]
  | ['event', string, Record<string, unknown>?]
  | ['consent', 'default' | 'update', Record<string, 'granted' | 'denied'>];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...command: GtagCommand) => void;
  }
}

let initialized = false;
let consentListenerAttached = false;
let scriptInjected = false;

const gaDisableKey = (measurementId: string) => `ga-disable-${measurementId}`;

const setGaDisabled = (disabled: boolean): void => {
  if (!isBrowser || !GA_MEASUREMENT_ID) {
    return;
  }

  (window as unknown as Record<string, boolean>)[gaDisableKey(GA_MEASUREMENT_ID)] = disabled;
};

const isLighthouseAudit = (): boolean => (
  typeof navigator !== 'undefined' && /Chrome-Lighthouse|Lighthouse/i.test(navigator.userAgent)
);

const normalizeEventName = (eventType: AnalyticsEventType): string => eventType.replace(/[^a-zA-Z0-9_]/g, '_');

const sanitizeGoogleParams = (
  params: Record<string, AnalyticsMetadataValue | string | undefined>
): Record<string, string | number | boolean> => {
  const entries: Array<[string, string | number | boolean]> = [];

  for (const [key, value] of Object.entries(params)) {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      entries.push([key, value]);
    }
  }

  return Object.fromEntries(entries);
};

const cookieDomains = (): string[] => {
  if (!isBrowser) {
    return [];
  }

  const hostname = window.location.hostname;
  const parts = hostname.split('.').filter(Boolean);
  const domains = new Set<string>(['']);

  for (let index = 0; index < parts.length - 1; index += 1) {
    domains.add(`.${parts.slice(index).join('.')}`);
  }

  return Array.from(domains);
};

const expireCookie = (name: string, domain: string): void => {
  const domainPart = domain ? `; domain=${domain}` : '';
  document.cookie = `${name}=; Max-Age=0; path=/${domainPart}; SameSite=Lax`;
};

export const removeGoogleAnalyticsCookies = (): void => {
  if (!isBrowser) {
    return;
  }

  const gaCookieNames = document.cookie
    .split(';')
    .map((cookie) => cookie.trim().split('=')[0])
    .filter((name) => name === '_ga' || name === '_gid' || name === '_gat' || name.startsWith('_ga_'));

  for (const name of gaCookieNames) {
    for (const domain of cookieDomains()) {
      expireCookie(name, domain);
    }
  }
};

const ensureGoogleTagShim = (): void => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function (..._command: GtagCommand): void {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(arguments);
  };
};

const gtag = (...command: GtagCommand): void => {
  ensureGoogleTagShim();
  window.gtag?.(...command);
};

const configureDeniedConsent = (): void => {
  if (!isBrowser || !GA_MEASUREMENT_ID) {
    return;
  }

  setGaDisabled(true);
  gtag('consent', 'update', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied'
  });
  removeGoogleAnalyticsCookies();
};

export const initializeGoogleAnalytics = (): void => {
  if (!isBrowser || initialized || isLighthouseAudit() || !GA_MEASUREMENT_ID || !hasAnalyticsConsent()) {
    return;
  }

  initialized = true;
  setGaDisabled(false);
  ensureGoogleTagShim();

  if (!scriptInjected && !document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`)) {
    scriptInjected = true;
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
    document.head.appendChild(script);
  }

  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied'
  });
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true
  });
};

export const setupGoogleAnalyticsConsentListener = (): void => {
  if (!isBrowser || consentListenerAttached || !GA_MEASUREMENT_ID) {
    return;
  }

  consentListenerAttached = true;

  if (hasAnalyticsConsent()) {
    initializeGoogleAnalytics();
  } else {
    configureDeniedConsent();
  }

  window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, ((event: CustomEvent<CookieConsentPreferences>) => {
    if (event.detail.analytics) {
      initializeGoogleAnalytics();
      window.gtag?.('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied'
      });
      return;
    }

    configureDeniedConsent();
  }) as EventListener);
};

export const trackGooglePageView = (input: {
  route: string;
  pageCategory: AnalyticsPageCategory;
  locale: SupportedLocale;
}): void => {
  if (!isBrowser || !GA_MEASUREMENT_ID || !hasAnalyticsConsent()) {
    return;
  }

  initializeGoogleAnalytics();
  window.gtag?.('event', 'page_view', {
    page_title: document.title,
    page_location: window.location.href,
    page_path: input.route,
    page_category: input.pageCategory,
    locale: input.locale
  });
};

export const trackGoogleAnalyticsEvent = (input: {
  eventType: AnalyticsEventType;
  route: string;
  pageCategory: AnalyticsPageCategory;
  locale: SupportedLocale;
  entityType?: AnalyticsEntityType;
  entityId?: string;
  entityName?: string;
  metadata?: Record<string, AnalyticsMetadataValue>;
}): void => {
  if (!isBrowser || !GA_MEASUREMENT_ID || !hasAnalyticsConsent()) {
    return;
  }

  if (input.eventType === 'page_view') {
    trackGooglePageView(input);
    return;
  }

  initializeGoogleAnalytics();
  window.gtag?.('event', normalizeEventName(input.eventType), sanitizeGoogleParams({
    page_path: input.route,
    page_category: input.pageCategory,
    locale: input.locale,
    entity_type: input.entityType,
    entity_id: input.entityId,
    entity_name: input.entityName,
    ...input.metadata
  }));
};
